import { defineStore } from 'pinia'
import { ref } from 'vue'
import debounce from 'debounce'
import { useLogError } from '@/composables/useLogError'
import { useHttpClient } from '@/composables/useHttpClient'
import { useMapStore } from '@/stores/useMapStore'
import { useDate } from '@/composables/useDate.ts'

export const usePortfolioStore = defineStore('portfolio', () => {
  const { logError } = useLogError()
  const { backendApi } = useHttpClient()
  const { moment } = useDate()

  // --- State ---

  const cubes = ref<string[]>([])
  const loadProfile = ref<number>(1)
  const consumption = ref<number>(5000)
  const profileCategories = ref<any[]>([])
  const date = ref<any>(moment().subtract(1, 'days'))
  const chartData = ref<Record<string, any>>({})
  const dayPrice = ref<number>(0)
  const dayConsumption = ref<number>(0)
  const highlightedNodes = ref<any[]>([])
  const communitySharePercent = ref<number>(0)

  // --- Getters ---

  function isCubeInPortfolio(cubeName: string): boolean {
    return cubes.value.includes(cubeName)
  }

  // --- Private ---

  function applyCalculationResult({ summary, chartData: cd, clpData, priceDatakWh, dayPrice: dp, totalConsumptionkWh }: any) {
    chartData.value = { chartData: cd, clpData, priceDatakWh }
    dayPrice.value = dp
    dayConsumption.value = totalConsumptionkWh
    communitySharePercent.value = summary.communitySharePercent
  }

  async function setCustomerCLP() {
    try {
      await backendApi.post('/portfolio/CustomerCLP', {
        clpType: loadProfile.value,
        factor: consumption.value / 1000,
      })
      await calculatePortfolio()
    } catch (err) {
      logError(`Error setting portfolio CLP: ${err}.`)
    }
  }

  const debouncedSetCustomerCLP = debounce(setCustomerCLP, 500)

  // --- Actions ---

  async function loadPortfolioSession() {
    try {
      const result = await backendApi.get('/portfolio/SessionData')
      for (const cubeName in result.data.cubes) {
        cubes.value = [...new Set([...cubes.value, cubeName])]
      }
      loadProfile.value = result.data.clp.clpType
      consumption.value = result.data.clp.factor * 1000
      await calculatePortfolio()
    } catch (err) {
      logError(`Error loading portfolio session: ${err}.`)
    }
  }

  async function initPortfolioSession() {
    try {
      await backendApi.get('/portfolio/sessionInit')
    } catch (err) {
      logError(`Error initializing portfolio session: ${err}.`)
    }
  }

  async function loadPortfolioCategories() {
    try {
      const result = await backendApi.get('/portfolio/CLPCategories')
      if (!result?.data) throw new Error('No data received')
      profileCategories.value = result.data.data
    } catch (err) {
      logError(`Error loading portfolio categories: ${err}.`)
    }
  }

  async function addCubeToPortfolio(cubeName: string) {
    try {
      cubes.value = [...new Set([...cubes.value, cubeName])]
      const mapStore = useMapStore()
      const price = mapStore.getProducerPrices(cubeName)?.currentPrice
      await backendApi.put(`/portfolio/Cube/${cubeName}/${price}`)
      await calculatePortfolio()
    } catch (err) {
      logError(`Error adding cube to portfolio: ${err}.`)
    }
  }

  async function removeCubeFromPortfolio(cubeName: string) {
    try {
      cubes.value = cubes.value.filter((c) => c !== cubeName)
      await backendApi.delete(`/portfolio/Cube/${cubeName}`)
      await calculatePortfolio()
    } catch (err) {
      logError(`Error removing cube from portfolio: ${err}.`)
    }
  }

  function setLoadProfile(profile: number) {
    loadProfile.value = profile
    debouncedSetCustomerCLP()
  }

  function setPortfolioConsumption(value: number) {
    consumption.value = value
    debouncedSetCustomerCLP()
  }

  async function calculatePortfolio() {
    try {
      const formattedDate = moment(date.value).format('YYYY-MM-DD')
      const result = await backendApi.get(`/portfolio/Calculation/${formattedDate}`)
      applyCalculationResult(result.data.data)
    } catch (err) {
      logError(`Error calculating portfolio: ${err}.`)
    }
  }

  async function setPortfolioDate(newDate: any) {
    date.value = newDate
    await calculatePortfolio()
  }

  return {
    // State
    cubes,
    loadProfile,
    consumption,
    profileCategories,
    date,
    chartData,
    dayPrice,
    dayConsumption,
    highlightedNodes,
    communitySharePercent,
    // Getters
    isCubeInPortfolio,
    // Actions
    loadPortfolioSession,
    initPortfolioSession,
    loadPortfolioCategories,
    addCubeToPortfolio,
    removeCubeFromPortfolio,
    setLoadProfile,
    setPortfolioConsumption,
    calculatePortfolio,
    setPortfolioDate,
  }
})
